/**
 * Issue #1600: pure view-model + polling-gate helpers for the async bulk-rescore
 * flow (was #1576/#1581 synchronous cap-25 loop).
 *
 * Extracted as pure functions — no React state/effects — so the "the modal
 * releases immediately" behaviour and the poll-cadence/stop logic are
 * unit-testable without a DOM (frontend vitest has no jsdom — see
 * docs/subsystems/testing.md / reference_frontend_no_jsdom). Mirrors the
 * microLessonsPolling.js pattern used for the debrief-summary poll (#470).
 */

/**
 * Decide what RescoreOfferModal shows, given the parent's rescore state.
 *
 * Load-bearing behaviour: the modal is NEVER gated on job completion. As soon
 * as POST /:id/rescore resolves and hands back a job (any status — 'running'
 * or the total===0 'done' short-circuit), the offer is considered "released"
 * — the facilitator can Continue/navigate away immediately. This is the
 * concrete difference from the old synchronous flow, where the modal stayed
 * on a blocking "Rescoring…" state until the whole batch finished.
 *
 * @param {{status:string, total:number}|null} rescoreJob - job returned by
 *   ScenariosAPI.rescoreRuns, or null before any rescore has been triggered.
 * @param {string|null} rescoreError - set if the kickoff POST itself failed
 *   (network/5xx) — distinct from a per-run failure inside the background job.
 * @returns {{view:'confirm'|'error'|'started', message?:string}}
 */
export function deriveRescoreModalView(rescoreJob, rescoreError) {
  if (rescoreError) {
    return { view: 'error', message: rescoreError };
  }
  if (!rescoreJob) {
    return { view: 'confirm' };
  }
  const message = rescoreJob.total === 0
    ? 'No existing runs to rescore.'
    : `Rescore started — running in background (${rescoreJob.total} run${rescoreJob.total === 1 ? '' : 's'}). We'll notify you when it's done.`;
  return { view: 'started', message };
}

/**
 * Whether the page should keep polling GET /:id/rescore-status. Only a
 * 'running' job needs polling — 'done'/'failed'/'idle' (or no job at all) are
 * all terminal/no-op for polling purposes.
 *
 * @param {{status:string}|null} rescoreJob
 * @returns {boolean}
 */
export function shouldPollRescoreStatus(rescoreJob) {
  return !!rescoreJob && rescoreJob.status === 'running';
}

/**
 * Format the toast shown when a background rescore job reaches a terminal
 * state (polled via GET /:id/rescore-status). Returns null for a non-terminal
 * status (nothing to announce yet).
 *
 * @param {{status:string, total?:number, rescored?:number, failed?:number, error?:string}} job
 * @returns {{message:string, type:'success'|'error'}|null}
 */
export function formatRescoreCompletionToast(job) {
  if (!job) return null;
  if (job.status === 'done') {
    const failedNote = job.failed > 0 ? ` (${job.failed} could not be rescored)` : '';
    return { message: `Rescore complete: ${job.rescored} of ${job.total} rescored${failedNote}`, type: 'success' };
  }
  if (job.status === 'failed') {
    return { message: `Rescore job failed${job.error ? `: ${job.error}` : ''}`, type: 'error' };
  }
  return null;
}
